import { Bounds } from '@ardoq/graph';
import {
  BlocksViewLink,
  BlocksViewNode,
  BlocksViewVisualState,
  ItemSet,
} from '../types';
import { isLink, isNode } from '../viewModel$/util';

function* nodes(set: Set<BlocksViewNode | BlocksViewLink>) {
  for (const item of set.values()) {
    if (isNode(item)) {
      yield item;
    }
  }
}

function* links(set: Set<BlocksViewNode | BlocksViewLink>) {
  for (const item of set.values()) {
    if (isLink(item)) {
      yield item;
    }
  }
}

export const createItemSet = (visualState: BlocksViewVisualState): ItemSet => {
  const set = new Set<BlocksViewNode | BlocksViewLink>();
  let node: BlocksViewNode | null | undefined; // = undefined;
  let link: BlocksViewLink | null | undefined; // = undefined;

  return {
    add: (item: BlocksViewNode | BlocksViewLink) => {
      item.visualState |= visualState;
      set.add(item);

      if (!node && isNode(item)) {
        node = item as BlocksViewNode;
      }

      if (!link && isLink(item)) {
        link = item as BlocksViewLink;
      }
    },

    delete: (item: BlocksViewNode | BlocksViewLink) => {
      item.visualState &= ~visualState;
      set.delete(item);

      if (item === node) {
        node = undefined;
      }

      if (item === link) {
        link = undefined;
      }
    },

    has: (item: BlocksViewNode | BlocksViewLink) => set.has(item),

    clear: () => {
      for (const item of set.values()) {
        item.visualState &= ~visualState;
      }

      set.clear();
      node = null;
      link = null;
    },

    values: () => set.values(),

    size: () => {
      return set.size;
    },

    isSubsetOf: (itemSet: ItemSet) => {
      for (const item of set.values()) {
        if (!itemSet.has(item)) {
          return false;
        }
      }

      return true;
    },

    node: () => {
      if (node === undefined) {
        const iterator = nodes(set).next();

        node = iterator.done ? null : iterator.value;
      }

      return node;
    },

    nodes: () => nodes(set),

    link: () => {
      if (link === undefined) {
        const iterator = links(set).next();

        link = iterator.done ? null : iterator.value;
      }

      return link;
    },

    links: () => links(set),

    bounds: () => {
      const bounds: Bounds = [+Infinity, +Infinity, -Infinity, -Infinity];

      for (const item of set.values()) {
        if (item.bounds) {
          bounds[0] = Math.min(bounds[0], item.bounds[0]);
          bounds[1] = Math.min(bounds[1], item.bounds[1]);
          bounds[2] = Math.max(bounds[2], item.bounds[2]);
          bounds[3] = Math.max(bounds[3], item.bounds[3]);
        }
      }

      return bounds;
    },
  };
};
